import { cn } from '@invent/wl-ui-kit-next';
import { Input } from '@invent/wl-ui-kit-next/ui';
import { LoaderCircle } from 'lucide-react';
import {
  useEffect,
  useId,
  useReducer,
  useRef,
  useState,
  type ComponentProps,
  type CSSProperties,
  type KeyboardEvent
} from 'react';
import { createPortal } from 'react-dom';

import { ApiError } from '@/domains/api';
import { surveyApi } from '../api';
import type { ICollegeSuggestion } from '../api/contracts';

/** How long the typing rests before a search goes out. */
const SEARCH_AFTER_MS = 150;
/** How many colleges a search offers. */
const SUGGESTIONS = 10;
/** The list's tallest, the space between it and its box, and the margin it keeps from the window's
 *  edge. */
const LIST_MAX_PX = 288;
const GAP_PX = 4;
const EDGE_PX = 8;
/** Under this much room below its box the list opens above it, where there is more. */
const ROOM_BELOW_PX = 160;

/**
 * What each search answered in this page's life, by its words — so a backspace, or a second box
 * typed the same, is answered at once. Suggestions only: what is stored is the text typed.
 */
const answered = new Map<string, ICollegeSuggestion[]>();
const ANSWERED_KEPT = 100;

function remember(words: string, found: ICollegeSuggestion[]) {
  answered.delete(words);
  answered.set(words, found);
  if (answered.size > ANSWERED_KEPT) answered.delete(answered.keys().next().value!);
}

/** Where the list is drawn — under its box, or above it when the room is there — or null while
 *  the box is scrolled out of the window. */
function placeBy(box: HTMLElement): CSSProperties | null {
  const at = box.getBoundingClientRect();
  if (at.bottom < 0 || at.top > window.innerHeight) return null;
  const below = window.innerHeight - at.bottom - GAP_PX - EDGE_PX;
  const above = at.top - GAP_PX - EDGE_PX;
  const up = below < ROOM_BELOW_PX && above > below;
  const width = Math.min(Math.max(at.width, 256), window.innerWidth - 2 * EDGE_PX);
  return {
    left: Math.max(EDGE_PX, Math.min(at.left, window.innerWidth - width - EDGE_PX)),
    width,
    maxHeight: Math.min(LIST_MAX_PX, up ? above : below),
    ...(up ? { bottom: window.innerHeight - at.top + GAP_PX } : { top: at.bottom + GAP_PX })
  };
}

/** A college's name with the words typed set in bold where they first occur. */
function Marked({ text, words }: { text: string; words: string }) {
  const at = words === '' ? -1 : text.toLowerCase().indexOf(words.toLowerCase());
  if (at < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, at)}
      <strong className="font-semibold">{text.slice(at, at + words.length)}</strong>
      {text.slice(at + words.length)}
    </>
  );
}

/**
 * A school's box that suggests US colleges as it is typed (the survey review's FA7, R13, T9) — one
 * box of a `SchoolList`, which draws a box per school. The answer stays the text typed: a
 * suggestion picked writes its name into the box, a school off the list is taken as typed, and the
 * box saves on blur like any other.
 *
 * **What is searched is the part after the last `;`**, so a box typed "Harvard; MIT" completes
 * the school being typed and keeps the one before it, until its list splits it on blur. A search
 * waits for the typing to rest, a new one aborts the one still out, and words searched before in
 * this page are answered from memory. The list keeps its last answer while the next search is out,
 * dimmed if the wait is long, and says "Searching…" while it has nothing to show; a search that
 * finds nothing says so.
 *
 * **A failed search says nothing**: the suggestions are a help to typing, the typing is kept, and
 * there is nothing the respondent can do about the search; the list simply stays shut. A
 * failure that is not survey-api's answer — the network's, or a fault of this code — is logged.
 *
 * The box is an ARIA combobox: ArrowDown and ArrowUp move through the list, keeping the highlighted
 * college in view, Enter or a click picks — Enter then goes no further, so the form's own Enter rule
 * never sees it — and Escape shuts it. The list is drawn over the page at the box's place (fixed,
 * in `body`), since a table cell's box sits in a scrolling table that would cut it off. It follows
 * the box through a scroll or a resize rather than shutting — the rejected alternative, which shut
 * it on any scroll, shut it under a wheel turned over the list itself — opens above a box with no
 * room under it, and is not drawn while the box is out of the window.
 */
export function SuggestBox({
  value,
  onChange,
  onBlur,
  className,
  ...props
}: Omit<ComponentProps<typeof Input>, 'value' | 'onChange'> & {
  value: string;
  onChange: (value: string) => void;
}) {
  const listId = useId();
  // The last search answered — the words it answered and its colleges — kept while the next is out.
  const [found, setFound] = useState<{ words: string; items: ICollegeSuggestion[] } | null>(null);
  const [searching, setSearching] = useState(false);
  const [active, setActive] = useState(-1);
  // The list is offered only while the respondent types — not for a value the box was drawn with,
  // nor for the name a pick just wrote — and until Escape.
  const [typing, setTyping] = useState(false);
  const box = useRef<HTMLInputElement>(null);
  const popup = useRef<HTMLDivElement>(null);
  // Where the pointer last moved over the list, to tell its own move from a scroll beneath it.
  const pointer = useRef<{ x: number; y: number } | null>(null);
  // Redraws the list at its box's place after a scroll or a resize.
  const [, follow] = useReducer((frame: number) => frame + 1, 0);

  const cut = value.lastIndexOf(';');
  const head = cut < 0 ? '' : value.slice(0, cut + 1);
  const query = value.slice(cut + 1).trim();

  useEffect(() => {
    if (!typing || query === '') {
      setSearching(false);
      return;
    }
    const known = answered.get(query);
    if (known) {
      setFound({ words: query, items: known });
      setActive(-1);
      setSearching(false);
      return;
    }
    setSearching(true);
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      surveyApi
        .collegeSuggestions(query, SUGGESTIONS, controller.signal)
        .then((items) => {
          remember(query, items);
          setFound({ words: query, items });
          setActive(-1);
          setSearching(false);
        })
        .catch((error: unknown) => {
          if (controller.signal.aborted) return;
          setFound(null);
          setSearching(false);
          // Said to nobody, but logged unless survey-api answered: a fault of this code (a 200 of
          // another shape) would otherwise read as "no matches" — as `useResponse` logs a lookup's.
          if (!(error instanceof ApiError)) {
            console.error('A college search failed in the client', error);
          }
        });
    }, SEARCH_AFTER_MS);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [typing, query]);

  const items = typing && query !== '' ? (found?.items ?? []) : [];
  const nothing = typing && !searching && found?.words === query && found.items.length === 0;
  const waiting = typing && searching && items.length === 0;
  const open = items.length > 0 || nothing || waiting;
  const hasActive = active >= 0 && active < items.length;

  useEffect(() => {
    if (!open) return;
    const moved = (event: Event) => {
      // The list's own scroll moves nothing under it.
      if (event.target instanceof Node && popup.current?.contains(event.target)) return;
      follow();
    };
    window.addEventListener('scroll', moved, true);
    window.addEventListener('resize', moved);
    return () => {
      window.removeEventListener('scroll', moved, true);
      window.removeEventListener('resize', moved);
    };
  }, [open]);

  const optionId = (index: number) => `${listId}-${index}`;

  useEffect(() => {
    if (active >= 0) document.getElementById(optionId(active))?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const pick = (item: ICollegeSuggestion) => {
    onChange(head ? `${head} ${item.name}` : item.name);
    setTyping(false);
    setActive(-1);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!open) return;
    if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && items.length > 0) {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActive((current) => (current + step + items.length) % items.length);
    } else if (event.key === 'Enter' && hasActive) {
      event.preventDefault();
      event.stopPropagation();
      pick(items[active]);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setTyping(false);
    }
  };

  const place = open && box.current ? placeBy(box.current) : null;
  return (
    <>
      <Input
        {...props}
        ref={box}
        className={className}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={items.length > 0}
        aria-controls={listId}
        aria-activedescendant={hasActive ? optionId(active) : undefined}
        autoComplete="off"
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
          setTyping(true);
        }}
        onKeyDown={onKeyDown}
        onBlur={(event) => {
          setTyping(false);
          setActive(-1);
          onBlur?.(event);
        }}
      />
      {place &&
        createPortal(
          <div
            ref={popup}
            data-suggest
            style={place}
            className={cn(
              'fixed z-50 flex flex-col overflow-hidden rounded-md border border-border',
              'bg-popover text-sm shadow-md'
            )}
          >
            {items.length > 0 && (
              <ul
                id={listId}
                role="listbox"
                aria-label="Colleges"
                aria-busy={searching}
                // The last answer dims while the next search is out, after a beat: an answer
                // that comes quickly, as most do, shows no dimming at all.
                className={cn(
                  'min-h-0 overflow-y-auto overscroll-contain p-1 transition-opacity',
                  searching ? 'opacity-60 delay-300' : 'delay-0'
                )}
              >
                {items.map((item, index) => (
                  <li
                    key={`${item.name}|${item.city}|${item.state}`}
                    id={optionId(index)}
                    role="option"
                    aria-selected={index === active}
                    // Down, not click: the box keeps its focus, so its blur does not shut the list
                    // first.
                    onMouseDown={(event) => {
                      event.preventDefault();
                      pick(item);
                    }}
                    // A pointer that moved, not a list the keys scrolled beneath a resting one (a
                    // browser may send a move for that too): the highlight the keys gave stays.
                    onMouseMove={(event) => {
                      const at = { x: event.clientX, y: event.clientY };
                      if (pointer.current?.x === at.x && pointer.current.y === at.y) return;
                      pointer.current = at;
                      setActive(index);
                    }}
                    className={cn(
                      'cursor-pointer rounded-sm px-2 py-1.5',
                      index === active && 'bg-accent text-accent-foreground'
                    )}
                  >
                    <span className="block">
                      <Marked text={item.name} words={query} />
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {item.city}, {item.state}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {(waiting || nothing) && (
              <p
                role="status"
                className="flex items-center gap-1.5 px-3 py-2 text-muted-foreground"
              >
                {waiting && (
                  <LoaderCircle
                    className="size-3.5 animate-spin motion-reduce:animate-none"
                    aria-hidden
                  />
                )}
                {waiting ? 'Searching…' : `No college matches “${query}”. What you typed is kept.`}
              </p>
            )}
          </div>,
          document.body
        )}
    </>
  );
}
